<template>
  <section class="career-section modern-career-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title modern-title">Career Highlights</h2>
        <p class="section-description modern-description">{{ description }}</p>
        <div class="section-divider"></div>
      </div>

      <v-row class="highlights-grid">
        <v-col
          v-for="(highlight, index) in highlights"
          :key="highlight.title"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            class="highlight-card modern-highlight-card"
            :class="{ 'is-hovered': highlight.isHovered }"
            @mouseenter="handleCardHover(highlight, true)"
            @mouseleave="handleCardHover(highlight, false)"
            elevation="0"
          >
            <!-- Enhanced Image Container -->
            <div class="highlight-image-container modern-image-container">
              <img
                :src="highlight.img"
                :alt="highlight.title"
                class="highlight-image modern-image"
              >

              <!-- Modern Overlay with Multiple Icons -->
              <div class="highlight-overlay modern-overlay">
                <div class="overlay-content">
                  <v-icon color="white" size="42" class="primary-icon">
                    {{ getHighlightIcon(highlight.title) }}
                  </v-icon>
                  <div class="overlay-text">
                    <span class="overlay-title">{{ highlight.title }}</span>
                    <span class="overlay-date">{{ highlight.date }}</span>
                  </div>
                </div>
              </div>

              <!-- Gradient Overlay -->
              <div class="gradient-overlay"></div>

              <!-- Corner Badge -->
              <div class="corner-badge" :style="getBadgeStyle(index)">
                <v-icon size="16" color="white">mdi-star</v-icon>
              </div>
            </div>

            <!-- Enhanced Card Content -->
            <div class="highlight-content modern-content">
              <v-card-title class="highlight-title modern-card-title">
                <span class="title-text">{{ highlight.title }}</span>
                <v-chip
                  small
                  :color="getDateColor(highlight.date)"
                  class="date-chip"
                  text-color="white"
                >
                  {{ highlight.date }}
                </v-chip>
              </v-card-title>

              <v-card-text class="highlight-description modern-card-description">
                <p class="description-text">{{ highlight.description }}</p>

                <!-- Achievement Metrics (if available) -->
                <div v-if="highlight.metrics" class="achievement-metrics">
                  <div
                    v-for="(metric, metricIndex) in highlight.metrics"
                    :key="metricIndex"
                    class="metric-item"
                  >
                    <v-icon small :color="metric.color" class="metric-icon">
                      {{ metric.icon }}
                    </v-icon>
                    <span class="metric-text">{{ metric.text }}</span>
                  </div>
                </div>

                <!-- Skills Tags -->
                <div v-if="highlight.skills" class="skills-tags">
                  <v-chip
                    v-for="(skill, skillIndex) in highlight.skills"
                    :key="skillIndex"
                    x-small
                    outlined
                    class="skill-chip"
                    :style="getSkillChipStyle(skillIndex)"
                  >
                    {{ skill }}
                  </v-chip>
                </div>
              </v-card-text>

              <!-- Modern Progress Indicator -->
              <div class="career-progress">
                <div class="progress-dots">
                  <div
                    v-for="dot in 5"
                    :key="dot"
                    class="progress-dot"
                    :class="{ 'active': dot <= getCareerLevel(highlight.title) }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Card Glow Effect -->
            <div class="card-glow" :style="getGlowStyle(index)"></div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Timeline Connector -->
      <div class="timeline-connector" data-aos="fade-up" data-aos-delay="400">
        <div class="connector-line"></div>
        <div class="connector-dots">
          <div
            v-for="(highlight, index) in highlights"
            :key="`dot-${index}`"
            class="timeline-dot"
            :class="{ 'active': index <= currentTimelineDot }"
            :style="getTimelineDotStyle(index)"
          >
            <v-icon size="16" color="white">{{ getTimelineIcon(index) }}</v-icon>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "CareerHighlightsSection",
  props: {
    highlights: {
      type: Array,
      required: true,
      default: () => []
    },
    description: {
      type: String,
      default: "Key milestones and achievements in my professional journey"
    }
  },
  data() {
    return {
      currentTimelineDot: 0,
      colors: ['#4361ee', '#f72585', '#4cc9f0', '#7209b7', '#f77f00'],
      icons: ['mdi-briefcase', 'mdi-code-tags', 'mdi-school', 'mdi-rocket-launch', 'mdi-trophy']
    };
  },
  methods: {
    handleCardHover(highlight, isHovered) {
      this.$set(highlight, 'isHovered', isHovered);
    },

    getHighlightIcon(title) {
      if (title.toLowerCase().includes('senior')) return 'mdi-account-tie';
      if (title.toLowerCase().includes('full stack')) return 'mdi-layers-triple';
      if (title.toLowerCase().includes('intern')) return 'mdi-school';
      if (title.toLowerCase().includes('lead')) return 'mdi-account-group';
      return 'mdi-briefcase-outline';
    },

    getBadgeStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        background: `linear-gradient(135deg, ${color}, ${color}CC)`,
        boxShadow: `0 4px 12px ${color}40`
      };
    },

    getDateColor(date) {
      if (date.includes('Present')) return 'success';
      const year = parseInt(date.split(' - ')[1]);
      if (year >= 2022) return 'primary';
      if (year >= 2020) return 'info';
      return 'secondary';
    },

    getSkillChipStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        borderColor: color,
        color: color
      };
    },

    getCareerLevel(title) {
      if (title.toLowerCase().includes('senior')) return 5;
      if (title.toLowerCase().includes('lead')) return 4;
      if (title.toLowerCase().includes('full stack')) return 4;
      if (title.toLowerCase().includes('developer')) return 3;
      return 2;
    },

    getGlowStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        background: `radial-gradient(circle at center, ${color}20, transparent 70%)`,
        opacity: 0
      };
    },

    getTimelineDotStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        background: `linear-gradient(135deg, ${color}, ${color}DD)`,
        boxShadow: `0 4px 16px ${color}60`
      };
    },

    getTimelineIcon(index) {
      return this.icons[index % this.icons.length];
    },

    animateTimeline() {
      let currentDot = 0;
      const animate = () => {
        if (currentDot < this.highlights.length) {
          this.currentTimelineDot = currentDot;
          currentDot++;
          setTimeout(animate, 300);
        }
      };
      setTimeout(animate, 1000);
    }
  },

  mounted() {
    // Initialize highlight states
    this.highlights.forEach(highlight => {
      this.$set(highlight, 'isHovered', false);
    });

    // Start timeline animation
    this.animateTimeline();
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.career-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 20% 80%, rgba(67, 97, 238, 0.05), transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(72, 149, 239, 0.05), transparent 50%);
    pointer-events: none;
  }
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.modern-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, $accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.section-divider {
  width: 100px;
  height: 4px;
  background: linear-gradient(90deg, $primary, $accent);
  margin: 0 auto;
  border-radius: 2px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, $primary, $accent);
    border-radius: 4px;
    opacity: 0.3;
    filter: blur(4px);
  }
}

.highlights-grid {
  margin-bottom: 3rem;
}

// Modern Highlight Card
.modern-highlight-card {
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;

  .theme--light & {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow: 0 8px 32px rgba(67, 97, 238, 0.08);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(255,255,255,0.1), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &.is-hovered {
    transform: translateY(-20px) scale(1.03);
    border-color: rgba(67, 97, 238, 0.4);
    box-shadow:
      0 30px 60px rgba(0, 0, 0, 0.4),
      0 0 50px rgba(67, 97, 238, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    .theme--light & {
      box-shadow:
        0 30px 60px rgba(67, 97, 238, 0.15),
        0 0 50px rgba(67, 97, 238, 0.1);
      border-color: rgba(67, 97, 238, 0.2);
    }

    &::before {
      opacity: 1;
    }

    .card-glow {
      opacity: 1 !important;
      transform: scale(1.2);
    }

    .modern-overlay {
      opacity: 1;
    }

    .modern-image {
      transform: scale(1.15);
    }

    .corner-badge {
      transform: scale(1.1);
    }
  }
}

// Modern Image Container
.modern-image-container {
  position: relative;
  height: 220px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), transparent);
    z-index: 2;
  }
}

.modern-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
  filter: brightness(0.9) contrast(1.1);

  .theme--light & {
    filter: brightness(1) contrast(1);
  }
}

.modern-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(67, 97, 238, 0.3));
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 3;
}

.overlay-content {
  text-align: center;
  transform: translateY(20px);
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

  .modern-highlight-card.is-hovered & {
    transform: translateY(0);
  }
}

.overlay-title {
  display: block;
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  margin-top: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.overlay-date {
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

.gradient-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  z-index: 2;
}

.corner-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

// Modern Card Content
.modern-content {
  padding: 0;
}

.modern-card-title {
  padding: 1.5rem 2rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.title-text {
  font-weight: 700;
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;
  line-height: 1.3;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.date-chip {
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 12px;
  height: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.modern-card-description {
  padding: 0 2rem 2rem;
}

.description-text {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.achievement-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metric-text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.skills-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.skill-chip {
  font-size: 0.75rem;
  height: 22px;
  border-radius: 11px;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.career-progress {
  padding: 1rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .theme--light & {
    border-color: rgba(67, 97, 238, 0.1);
  }
}

.progress-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(67, 97, 238, 0.3);
  }

  &.active {
    background: linear-gradient(135deg, $primary, $accent);
    box-shadow: 0 0 10px rgba(67, 97, 238, 0.5);
    transform: scale(1.2);
  }
}

.card-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  transition: all 0.5s ease;
  pointer-events: none;
  z-index: -1;
}

// Timeline Connector
.timeline-connector {
  position: relative;
  margin-top: 2rem;
  padding: 2rem 0;
}

.connector-line {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, transparent, $primary, $accent, transparent);
  border-radius: 1px;
}

.connector-dots {
  display: flex;
  justify-content: center;
  gap: 3rem;
  position: relative;
  z-index: 1;
}

.timeline-dot {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 0.5;
  transform: scale(0.8);

  &.active {
    opacity: 1;
    transform: scale(1);
  }
}

// Responsive Design
@media (max-width: $breakpoint-md) {
  .modern-title {
    font-size: 2.5rem;
  }

  .modern-image-container {
    height: 200px;
  }

  .modern-card-title {
    padding: 1.2rem 1.5rem 0.8rem;
  }

  .modern-card-description {
    padding: 0 1.5rem 1.5rem;
  }

  .connector-dots {
    gap: 2rem;
  }

  .timeline-dot {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: $breakpoint-xs) {
  .career-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .modern-title {
    font-size: 2rem;
  }

  .modern-description {
    font-size: 1rem;
  }

  .modern-highlight-card {
    border-radius: 20px;

    &.is-hovered {
      transform: translateY(-12px) scale(1.02);
    }
  }

  .modern-image-container {
    height: 180px;
  }

  .modern-card-title {
    padding: 1rem 1.2rem 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .title-text {
    font-size: 1.1rem;
  }

  .modern-card-description {
    padding: 0 1.2rem 1.2rem;
  }

  .description-text {
    font-size: 0.9rem;
  }

  .connector-dots {
    gap: 1rem;
  }

  .timeline-dot {
    width: 32px;
    height: 32px;
  }

  .connector-line {
    width: 80%;
  }
}

// Animation Keyframes
@keyframes cardGlow {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0;
  }
}

// Dark/Light theme specific adjustments
.theme--light {
  .modern-overlay {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(67, 97, 238, 0.2));
  }

  .overlay-title {
    color: rgba(33, 37, 41, 0.9);
    text-shadow: none;
  }

  .overlay-date {
    color: rgba(73, 80, 87, 0.7);
  }

  .gradient-overlay {
    background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
  }
}
</style>